import React, { useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { useOperatorProfileStore } from '../../../stateManagers/stores/operatorProfileStore';

// ── Types ────────────────────────────────────────────────────────────────────
interface PaymentModeDetails {
  transaction_id?: string | null;
  upi_id?: string | null;
  reference_number?: string | null;
  cheque_number?: string | null;
  bank_name?: string | null;
  cheque_date?: string | null;
}

export interface ReceiptPayment {
  payment_id: string;
  amount_paid: number;
  discount_amount?: number;
  discount_reason?: string | null;
  payment_method: string;
  payment_mode_details?: PaymentModeDetails | null;
  receipt_number?: string | null;
  payment_date?: string | null;
  remarks?: string | null;
}

export interface ReceiptData {
  student_name: string;
  student_mobile?: string | null;
  enrollment_id: string;
  course_name?: string | null;
  batch_name?: string | null;
  batch_time?: string | null;
  date_of_admission?: string | null;
  total_fee: number;
  total_paid: number;
  balance_due: number;
  payment: ReceiptPayment;
}

interface ReceiptModalProps {
  visible: boolean;
  data: ReceiptData;
  onHide: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const HCI_LOGO = 'https://media.hciindia.in/email/new-hci-logo.png';

const fmt = (n: number) => `Rs.${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return d; }
};

const methodLabel: Record<string, string> = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card / Debit / Credit',
  bank_transfer: 'Bank Transfer / NEFT / RTGS',
  cheque: 'Cheque',
};

// ── Component ────────────────────────────────────────────────────────────────
const ReceiptModal: React.FC<ReceiptModalProps> = ({ visible, data, onHide }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const { profile } = useOperatorProfileStore();

  const instituteName = profile?.institute_name ?? 'Hindustan Computer Institute';
  const branchName    = profile?.branch_name    ?? '';

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Fee Receipt – ${data.payment.receipt_number || data.payment.payment_id}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #000; background: white; }
            @page { size: A4; margin: 18mm 16mm 18mm 16mm; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 350);
  };

  const { payment } = data;
  const discount = payment.discount_amount || 0;

  const receiptHtml = `
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body, .receipt-root { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #000; background: white; }
      .receipt-root { width: 100%; max-width: 760px; margin: 0 auto; }

      /* ── Header ── */
      .rh { display: flex; align-items: center; justify-content: space-between; padding-bottom: 14px; border-bottom: 3px solid #0e225d; margin-bottom: 14px; }
      .rh-logo { width: 130px; height: 50px; object-fit: contain; }
      .rh-title { text-align: center; flex: 1; padding: 0 16px; }
      .rh-title h1 { font-size: 20px; font-weight: 800; color: #0e225d; letter-spacing: -0.3px; line-height: 1.2; }
      .rh-title .branch { font-size: 12px; font-weight: 700; color: #1f2937; margin-top: 3px; }
      .rh-title .tagline { font-size: 10px; color: #374151; margin-top: 2px; }
      .rh-meta { text-align: right; font-size: 11px; color: #1f2937; line-height: 2; min-width: 140px; }
      .rh-meta strong { color: #0e225d; }

      /* ── Banner ── */
      .gold-bar { background: #0e225d; color: #fff; padding: 9px 16px; border-radius: 6px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
      .gold-bar .title { font-size: 13px; font-weight: 800; letter-spacing: 1px; color: #fff; }
      .gold-bar .rcpt-no { background: #fbb21f; color: #0e225d; padding: 3px 14px; border-radius: 20px; font-size: 11px; font-weight: 800; letter-spacing: 0.5px; }

      /* ── Info grid ── */
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
      .info-box { border: 1.5px solid #94a3b8; border-radius: 8px; padding: 12px 14px; }
      .info-box h4 { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.7px; color: #0e225d; margin-bottom: 9px; border-bottom: 1px solid #94a3b8; padding-bottom: 6px; }
      .info-row { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 5px; gap: 8px; }
      .info-row .lbl { color: #374151; font-weight: 500; white-space: nowrap; }
      .info-row .val { font-weight: 700; color: #0e225d; text-align: right; }

      /* ── Section title ── */
      .section-title { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.7px; color: #0e225d; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
      .section-title::before { content: ''; display: inline-block; width: 3px; height: 14px; background: #fbb21f; border-radius: 3px; }

      /* ── Payment table ── */
      table.pt { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 11px; }
      table.pt thead tr { background: #dde4f0; }
      table.pt th { padding: 9px 12px; text-align: left; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; color: #0e225d; border-bottom: 2px solid #0e225d; border-right: 1px solid #94a3b8; }
      table.pt th:last-child { border-right: none; }
      table.pt td { padding: 10px 12px; border-bottom: 1px solid #cbd5e1; border-right: 1px solid #cbd5e1; color: #000; vertical-align: top; }
      table.pt td:last-child { border-right: none; }
      table.pt tr:last-child td { border-bottom: none; }
      table.pt .sub { font-size: 10px; color: #374151; margin-top: 3px; }

      /* ── Totals ── */
      .totals-box { border: 1.5px solid #94a3b8; border-radius: 8px; overflow: hidden; margin-bottom: 18px; }
      .totals-row { display: flex; justify-content: space-between; padding: 9px 16px; font-size: 11px; border-bottom: 1px solid #cbd5e1; }
      .totals-row:last-child { border-bottom: none; }
      .totals-row .lbl { color: #374151; font-weight: 500; }
      .totals-row .val { font-weight: 700; color: #000; }
      .totals-row.highlight { background: #0e225d; }
      .totals-row.highlight .lbl { color: #fff; font-weight: 700; font-size: 12px; }
      .totals-row.highlight .val { color: #fbb21f; font-size: 14px; font-weight: 800; }
      .totals-row.balance-clear { background: #bbf7d0; }
      .totals-row.balance-clear .lbl { color: #14532d; font-weight: 700; }
      .totals-row.balance-clear .val { color: #14532d; font-weight: 800; }
      .totals-row.balance-due-row { background: #fecaca; }
      .totals-row.balance-due-row .lbl { color: #7f1d1d; font-weight: 700; }
      .totals-row.balance-due-row .val { color: #7f1d1d; font-weight: 800; }
      .totals-row.discount-row { background: #fde68a; }
      .totals-row.discount-row .lbl { color: #713f12; font-weight: 600; }
      .totals-row.discount-row .val { color: #713f12; font-weight: 700; }

      /* ── Footer ── */
      .footer-row { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 24px; padding-top: 16px; border-top: 1.5px dashed #64748b; }
      .footer-note { font-size: 10px; color: #374151; line-height: 1.8; max-width: 55%; }
      .footer-note strong { color: #0e225d; }
      .sign-box { text-align: center; }
      .sign-line { width: 150px; border-top: 1.5px solid #0e225d; padding-top: 6px; font-size: 10px; color: #374151; font-weight: 600; }
      .sign-institute { font-size: 10px; color: #0e225d; font-weight: 700; margin-top: 3px; }

      /* ── Status badge ── */
      .status-bar { text-align: center; margin-top: 18px; padding-top: 14px; border-top: 1px solid #94a3b8; }
      .badge { display: inline-block; padding: 4px 18px; border-radius: 20px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; border: 1.5px solid; }
      .badge-paid    { background: #bbf7d0; color: #14532d; border-color: #16a34a; }
      .badge-pending { background: #fde68a; color: #713f12; border-color: #ca8a04; }
      .gen-time { font-size: 9px; color: #374151; margin-top: 6px; }
    </style>

    <div class="receipt-root">

      <!-- Header -->
      <div class="rh">
        <img class="rh-logo" src="${HCI_LOGO}" alt="HCI Logo" />
        <div class="rh-title">
          <h1>${instituteName}</h1>
          ${branchName ? `<div class="branch">${branchName}</div>` : ''}
        </div>
        <div class="rh-meta">
          <div><strong>Date:</strong> ${fmtDate(payment.payment_date)}</div>
          <div><strong>Admission:</strong> ${fmtDate(data.date_of_admission)}</div>
        </div>
      </div>

      <!-- Banner -->
      <div class="gold-bar">
        <span class="title">FEE PAYMENT RECEIPT</span>
        <span class="rcpt-no">${payment.receipt_number || payment.payment_id}</span>
      </div>

      <!-- Info Grid -->
      <div class="info-grid">
        <div class="info-box">
          <h4>Student Information</h4>
          <div class="info-row"><span class="lbl">Name</span><span class="val">${data.student_name}</span></div>
          ${data.student_mobile ? `<div class="info-row"><span class="lbl">Mobile</span><span class="val">${data.student_mobile}</span></div>` : ''}
          <div class="info-row"><span class="lbl">Enrollment ID</span><span class="val">${data.enrollment_id}</span></div>
        </div>
        <div class="info-box">
          <h4>Course Information</h4>
          ${data.course_name ? `<div class="info-row"><span class="lbl">Course</span><span class="val">${data.course_name}</span></div>` : ''}
          ${data.batch_name  ? `<div class="info-row"><span class="lbl">Batch</span><span class="val">${data.batch_name}</span></div>` : ''}
          ${data.batch_time  ? `<div class="info-row"><span class="lbl">Timing</span><span class="val">${data.batch_time}</span></div>` : ''}
          ${branchName       ? `<div class="info-row"><span class="lbl">Branch</span><span class="val">${branchName}</span></div>` : ''}
        </div>
      </div>

      <!-- Payment Details -->
      <div class="section-title">Payment Details</div>
      <table class="pt">
        <thead>
          <tr>
            <th>Description</th>
            <th>Payment Method</th>
            <th>Reference / Receipt No.</th>
            <th style="text-align:right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Course Fee Payment</strong>
              <div class="sub">${[data.course_name, data.batch_name].filter(Boolean).join(' · ')}</div>
            </td>
            <td>
              <strong>${methodLabel[payment.payment_method] || payment.payment_method}</strong>
              ${payment.payment_mode_details?.upi_id          ? `<div class="sub">UPI ID: ${payment.payment_mode_details.upi_id}</div>` : ''}
              ${payment.payment_mode_details?.transaction_id  ? `<div class="sub">TXN: ${payment.payment_mode_details.transaction_id}</div>` : ''}
              ${payment.payment_mode_details?.cheque_number   ? `<div class="sub">Cheque No: ${payment.payment_mode_details.cheque_number}</div>` : ''}
              ${payment.payment_mode_details?.reference_number? `<div class="sub">Ref: ${payment.payment_mode_details.reference_number}</div>` : ''}
              ${payment.payment_mode_details?.bank_name       ? `<div class="sub">Bank: ${payment.payment_mode_details.bank_name}</div>` : ''}
              ${payment.payment_mode_details?.cheque_date     ? `<div class="sub">Cheque Date: ${fmtDate(payment.payment_mode_details.cheque_date)}</div>` : ''}
            </td>
            <td style="font-family:monospace;font-size:11px;font-weight:700;color:#0e225d;">
              ${payment.receipt_number || payment.payment_id}
            </td>
            <td style="text-align:right;font-weight:800;color:#0e225d;font-size:14px;">
              ${fmt(payment.amount_paid)}
            </td>
          </tr>
          ${discount > 0 ? `
          <tr style="background:#fde68a;">
            <td colspan="3">
              <strong style="color:#713f12;">Discount Applied</strong>
              ${payment.discount_reason ? `<div class="sub" style="color:#713f12;">${payment.discount_reason}</div>` : ''}
            </td>
            <td style="text-align:right;font-weight:800;color:#713f12;">− ${fmt(discount)}</td>
          </tr>` : ''}
          ${payment.remarks ? `
          <tr style="background:#f1f5f9;">
            <td colspan="4" style="font-size:10px;color:#374151;">
              <strong>Remarks:</strong> ${payment.remarks}
            </td>
          </tr>` : ''}
        </tbody>
      </table>

      <!-- Fee Summary -->
      <div class="section-title">Fee Summary</div>
      <div class="totals-box">
        <div class="totals-row">
          <span class="lbl">Total Course Fee</span>
          <span class="val">${fmt(data.total_fee)}</span>
        </div>
        <div class="totals-row">
          <span class="lbl">Previously Paid</span>
          <span class="val">${fmt(Math.max(0, data.total_paid - payment.amount_paid))}</span>
        </div>
        ${discount > 0 ? `
        <div class="totals-row discount-row">
          <span class="lbl">Discount (this receipt)</span>
          <span class="val">− ${fmt(discount)}</span>
        </div>` : ''}
        <div class="totals-row highlight">
          <span class="lbl">Amount Paid (this receipt)</span>
          <span class="val">${fmt(payment.amount_paid)}</span>
        </div>
        <div class="totals-row ${data.balance_due <= 0 ? 'balance-clear' : 'balance-due-row'}">
          <span class="lbl">Balance Due</span>
          <span class="val">${fmt(Math.max(0, data.balance_due))}</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="footer-row">
        <div class="footer-note">
          <strong>Note:</strong> This is a computer-generated receipt and does not require a physical signature.
          Please retain this receipt for your records.<br/>
          For queries, contact the institute administration.
        </div>
        <div class="sign-box">
          <div style="height:40px;"></div>
          <div class="sign-line">Authorised Signatory</div>
          <div class="sign-institute">${branchName || instituteName}</div>
        </div>
      </div>

      <!-- Status badge -->
      <div class="status-bar">
        <div>
          <span class="badge ${data.balance_due <= 0 ? 'badge-paid' : 'badge-pending'}">
            ${data.balance_due <= 0 ? '✓ FULLY PAID' : `BALANCE REMAINING: ${fmt(data.balance_due)}`}
          </span>
        </div>
        <div class="gen-time">Generated on ${new Date().toLocaleString('en-IN')}</div>
      </div>

    </div>
  `;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '90vw', maxWidth: '820px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header: { style: { display: 'none' } },
        content: { style: { padding: '0' } },
      }}
    >
      {/* Modal chrome */}
      <div style={{ background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', background: 'rgba(251,178,31,0.2)', border: '1px solid rgba(251,178,31,0.4)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="pi pi-file-pdf" style={{ color: '#fbb21f', fontSize: '0.85rem' }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: 'white' }}>Fee Receipt</p>
            <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>
              {payment.receipt_number || payment.payment_id} · {fmtDate(payment.payment_date)}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handlePrint}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: '#fbb21f', border: 'none', borderRadius: '9px', color: '#0e225d', fontSize: '0.82rem', fontWeight: '800', cursor: 'pointer', letterSpacing: '0.3px' }}
          >
            <i className="pi pi-print" style={{ fontSize: '0.82rem' }} />
            Print / Save PDF
          </button>
          <button
            onClick={onHide}
            style={{ width: '34px', height: '34px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.8rem' }} />
          </button>
        </div>
      </div>

      {/* Receipt preview */}
      <div style={{ padding: '24px', background: '#f5f7fa', overflowY: 'auto', maxHeight: '78vh' }}>
        <div
          ref={printRef}
          style={{ background: 'white', borderRadius: '12px', padding: '32px 36px', boxShadow: '0 4px 24px rgba(14,34,93,0.1)', border: '1px solid #e5e7eb' }}
          dangerouslySetInnerHTML={{ __html: receiptHtml }}
        />
      </div>
    </Dialog>
  );
};

export default ReceiptModal;